import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PageHeaderComponent } from "../page-header/page-header.component";
import { ApiServiceService } from '../../modules/services/api-service.service';
import { AuthenticationService } from '../../modules/services/authentication.service';
import alertify from 'alertifyjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileService } from '../../modules/services/file/file-service.service';
import { GenericPaginationResponseV2, GenericResponse } from '../../modules/shared/models/general.modal';
import { HttpErrorResponse } from '@angular/common/http';
import { MdaListResponse, MdaOption } from '../../modules/services/utility-service.models';
import { OverlayModule } from '@angular/cdk/overlay';
import { MdaDetailsResponse } from '../../modules/shared/models/mda.details';
import { SettingsService } from '../../modules/services/settings/settings.service';
import { CharLength } from '../../modules/directives/char-length.directive';

@Component({
  selector: 'app-mda-settings-modal',
  imports: [PageHeaderComponent, ReactiveFormsModule, OverlayModule, CharLength],
  templateUrl: './mda-settings-modal.html',
  styleUrl: './mda-settings-modal.scss',
})
export class MdaSettingsModal implements OnInit {
  @Input({ required: true }) open = false;
  @Input() targetMdaId: string;
  @Output() close = new EventEmitter<void>();

  @ViewChild("logoField") logoField: ElementRef<HTMLInputElement>;
  @ViewChild("signatureField") signatureField: ElementRef<HTMLInputElement>;

  settingsForm: FormGroup;
  mode: "CREATE" | "UPDATE" = "CREATE";
  isLoadingSettings: boolean = false;
  isSubmitting: boolean = false;
  isFormValid: boolean = false;

  mdaOptions: MdaOption[] = [];

  logoPreview: string | null = null;
  signaturePreview: string | null = null;

  isDraggingSignatureFile: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly fileService: FileService,
    private readonly apiService: ApiServiceService,
    private readonly settingsService: SettingsService,
    private readonly authenticationService: AuthenticationService
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnInit(): void {
    this.fetchMdas();
    this.fetchSettings();
    this.initializeForm();
  }

  initializeForm() {
    const mdaId = this.authenticationService.getCurrentUserMda();
    this.settingsForm = this.fb.group({
      logo: this.fb.control({value: null, disabled: false}, {validators: [Validators.required]}),
      acronym: this.fb.control({value: '', disabled: false}, {validators: [Validators.required]}),
      headName: this.fb.control({value: '', disabled: false}, {validators: [Validators.required]}),
      headTitle: this.fb.control({value: '', disabled: false}, {validators: [Validators.required]}),
      headSignature: this.fb.control({value: null, disabled: false}, {validators: [Validators.required]}),
      primaryColor: this.fb.control({value: '#191615', disabled: false}, {validators: [Validators.required]}),
      secondaryColor: this.fb.control({value: '#1697B4', disabled: false}, {validators: [Validators.required]}),
      primaryTextColor: this.fb.control({value: '#595A69', disabled: false}, {validators: [Validators.required]}),
      secondaryTextColor: this.fb.control({value: '#FFFFFF', disabled: false}, {validators: [Validators.required]}),
      mdaId: this.fb.control({value: this.targetMdaId ?? mdaId ?? '', disabled: true}, {validators: [Validators.required]}),
    })

    this.settingsForm.statusChanges.subscribe({
      next: (status) =>  {
        this.isFormValid = status === "VALID";
      }
    })
  }

  get logo() {return this.settingsForm.get('logo')}
  get mdaId() {return this.settingsForm.get('mdaId')}
  get acronym() {return this.settingsForm.get('acronym')}
  get headName() {return this.settingsForm.get('headName')}
  get headTitle() {return this.settingsForm.get('headTitle')}
  get primaryColor() {return this.settingsForm.get('primaryColor')}
  get headSignature() {return this.settingsForm.get('headSignature')}
  get secondaryColor() {return this.settingsForm.get('secondaryColor')}
  get primaryTextColor() {return this.settingsForm.get('primaryTextColor')}
  get secondaryTextColor() {return this.settingsForm.get('secondaryTextColor')}

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open) return;
    this.close.emit();
  }

  fetchSettings() {
    this.isLoadingSettings = true;
    const payload = {
      page: 1,
      size: 1,
      mdaId: this.targetMdaId
    }

    this.apiService.post('/mda/details/search', payload).subscribe({
      next: (response: GenericPaginationResponseV2<MdaDetailsResponse>) => {
        if (response.status !== "success") {
          alertify.error(response.message || "Failed to fetch MDA details");
          this.isLoadingSettings = false;
        } else {
          if (response.data.content) {
            const mdaDetails = response.data.content[0];
            this.populateSettingsForm(mdaDetails);
          }
        }
        this.isLoadingSettings = false;
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "An error occured while fetching MDA settings");
        } else {
          console.log(error);
          alertify.error(error || "An error occured while fetching MDA settings");
        }
        this.isLoadingSettings = false;
        this.mode = "CREATE";
      }
    })
  }

  fetchMdas() {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }

  populateSettingsForm(details: MdaDetailsResponse) {
    this.mode = "UPDATE";

    // const mdaId = this.authenticationService.getCurrentUserMda();
    this.settingsForm.patchValue(
      {
        mdaId: details.mdaId,
        acronym: details.acronym,
        headName: details.headName,
        headTitle: details.headTitle,
        primaryColor: details.primaryColor || this.settingsService.BG_PRIMARY_COLOR,
        secondaryColor: details.secondaryColor || this.settingsService.BG_SECONDARY_COLOR,
        primaryTextColor: details.primaryTextColor || this.settingsService.TEXT_PRIMARY_COLOR,
        secondaryTextColor: details.secondaryTextColor || this.settingsService.TEXT_SECONDARY_COLOR,
      }
    )

    this.logoPreview = details.logo;
    this.signaturePreview = details.headSignature;

    ['logo', 'headSignature'].forEach(field => {
      const control = this.settingsForm.get(field);

      control?.removeValidators(Validators.required);
      control?.updateValueAndValidity();
    });
  }

  selectSignatureImage() {
    this.signatureField?.nativeElement?.click();
  }

  selectLogoImage() {
    this.logoField?.nativeElement?.click();
  }

  async extractLogoImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      this.removeLogoImage();
      return;
    }

    const dataUrl = await this.fileService.toBase64(file);

    if (!dataUrl) {
      this.removeLogoImage();
      return;
    };

    this.logoPreview = dataUrl;
    this.settingsForm.get('logo').setValue(this.fileService.extractBase64(dataUrl));
  }

  removeLogoImage() {
    this.logoPreview = null;
    this.settingsForm.get('logo').reset();
  }

  async extractSignatureImage(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      this.removeSignatureImage();
      return;
    }

    const dataUrl = await this.fileService.toBase64(file);

    if (!dataUrl) {
      this.removeSignatureImage();
      return;
    };

    this.signaturePreview = dataUrl;
    this.settingsForm.get('headSignature').setValue(this.fileService.extractBase64(dataUrl));
  }

  removeSignatureImage() {
    this.signaturePreview = null;
    this.settingsForm.get('headSignature').reset();
  }

  closeModal() {
    this.close.emit();
  }

  submit() {
    if (this.mode === "CREATE") {
      this.submitForCreate();
      return;
    }

    if (this.mode === "UPDATE") {
      this.submitForUpdate();
      return;
    }
  }

  submitForCreate(){
    this.settingsForm.markAllAsTouched();
    if (!this.settingsForm.valid) return;
    
    this.isSubmitting = true;
    const payload = this.sanitizePayload(this.settingsForm.getRawValue());
    this.apiService.post('/mda/details', payload).subscribe({
      next: (response: GenericResponse) => {
        if (response.status !== "success") {
          alertify.error(response.message || "An error occured during MDA creation");
          this.isSubmitting = false;
          return;
        }

        alertify.success(response.message || "Successfully created MDA");
        this.isSubmitting = false;
        this.close.emit();
        this.settingsService.resolveApplicationColorScheme();
      }, 
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "An error occured during MDA creation");
        } else {
          alertify.error(error || "An error occured during MDA creation");
        }
        this.isSubmitting = false;
      }
    })
  }

  submitForUpdate(){
    if (!this.settingsForm.valid || !this.logoPreview || !this.signaturePreview) return;
    
    this.isSubmitting = true;
    const payload = this.sanitizePayload(this.settingsForm.getRawValue());
    this.apiService.put('/mda/details', payload).subscribe({
      next: (response: GenericResponse) => {
        if (response.status !== "success") {
          alertify.error(response.message || "An error occured during MDA creation");
          this.isSubmitting = false;
          return;
        }

        alertify.success(response.message || "Successfully updated MDA details");
        this.isSubmitting = false;
        this.close.emit();
        this.settingsService.resolveApplicationColorScheme();
      }, 
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "An error occured during MDA update");
        } else {
          alertify.error(error || "An error occured during MDA update");
        }
        this.isSubmitting = false;
      }
    })
  }

  sanitizePayload(payload: Record<string, any>) {
    return Object.fromEntries(
        Object.entries(payload).filter(([key, value]) => {
          if (typeof value === "string") return value.length > 0;
          if (typeof value === "number") return true;
          if (!value) return false;
          return true;
      })
    )
  }

  onDrop(event: DragEvent) {
    event.preventDefault();

    this.isDraggingSignatureFile = false;
    const dt = event.dataTransfer;
    const files = dt?.files;

    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingSignatureFile = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingSignatureFile = false;
  }

  async handleFiles(files: FileList) {
  const targetFile = files[0];
  const dataUrl = await this.fileService.toBase64(targetFile);

  this.signaturePreview = dataUrl;
  this.settingsForm.get('headSignature').setValue(this.fileService.extractBase64(dataUrl));
}
}
